import { DatabaseFieldType, type DatabaseValue } from '@databases/types/database';
import { createElement, type ReactNode } from 'react';

import { defineFieldReadModule, type FieldReadModule } from '../field-read-module.ts';
import { type CheckboxReadContent } from './checkbox-read-content.ts';
import { CheckboxValueView } from './checkbox-value-view.tsx';

export type CheckboxReadPolicy = {
  readonly checkedLabel: string;
  readonly uncheckedLabel: string;
};

export type CheckboxReadRenderer = (content: CheckboxReadContent) => ReactNode;

const renderCheckbox: CheckboxReadRenderer = (content) =>
  createElement(CheckboxValueView, { content });

/**
 * Each compiled field retains two immutable states, independent of row count.
 * Labels come from composition so mounted values never read locale or app state.
 */
export function createCheckboxFieldReadModule({
  checkedLabel,
  uncheckedLabel,
  render = renderCheckbox
}: CheckboxReadPolicy & {
  readonly render?: CheckboxReadRenderer;
}): FieldReadModule<CheckboxReadContent> {
  return defineFieldReadModule<CheckboxReadContent>({
    fieldType: DatabaseFieldType.CHECKBOX,
    render,
    compile(field) {
      const checkedContent = Object.freeze<CheckboxReadContent>({
        kind: 'checkbox',
        checked: true,
        label: checkedLabel
      });
      const uncheckedContent = Object.freeze<CheckboxReadContent>({
        kind: 'checkbox',
        checked: false,
        label: uncheckedLabel
      });

      return Object.freeze({
        fieldId: field.id,
        resolve(
          storedValue: DatabaseValue | undefined,
          defaultValue: DatabaseValue | undefined
        ): CheckboxReadContent {
          const value = storedValue ?? defaultValue;

          return value?.type === DatabaseFieldType.CHECKBOX && value.value
            ? checkedContent
            : uncheckedContent;
        }
      });
    }
  });
}
